import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CalendarClock, AlertCircle, DollarSign, TrendingUp, Users, Clock, BellPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import CustomerDetailsDialog from './CustomerDetailsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  overdue: 'bg-red-500/20 text-red-500',
  today: 'bg-amber-500/20 text-amber-500',
  upcoming: 'bg-cyan-500/20 text-cyan-400',
  paid: 'bg-emerald-500/20 text-emerald-500',
};

/**
 * Role-agnostic dashboard widget. Fetches /api/dashboard/upcoming-due which
 * respects the caller's own scope (assigned customers for field roles, branch
 * for Branch Manager, everything for HQ) and renders:
 *   - 5 KPI cards (assigned, upcoming, due today, overdue, paid)
 *   - The Upcoming + Due list
 */
export default function UpcomingDueCustomers({ title = 'Upcoming & Due Installments' }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({ open: false, customerId: null, installmentId: null });

  // SE / RO are read-only on the Due/Upcoming widget — no click-through to
  // the customer profile, no inline payments.
  const isReadOnlyRow = user?.role === 'Sales Executive' || user?.role === 'Recovery Officer';

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/dashboard/upcoming-due`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => setData({ rows: [], stats: {} }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleRemind = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Push ${r.customer_name}'s overdue installment #${r.installment_no || 1} to next month?\n\nThe row will disappear from today's overdue list and reappear on the new date.`)) return;
    try {
      await axios.post(`${API_URL}/api/installments/remind-next-month/${r.installment_id}`,
        { installment_no: Number(r.installment_no || 1) }, { withCredentials: true });
      toast.success(`Reminded — ${r.customer_name} will reappear next month`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set reminder');
    }
  };

  if (loading) return <div className="text-slate-400 text-sm py-6" data-testid="upcoming-due-loading">Loading upcoming installments…</div>;

  const s = data?.stats || {};
  const canSeeRemaining = !!data?.can_view_remaining_amount;
  const cards = [
    { title: 'Assigned Customers', value: s.total_customers || 0, icon: Users, color: 'text-blue-500', testId: 'ud-total' },
    { title: 'Upcoming (7d)', value: `${s.upcoming_count || 0} · PKR ${(s.upcoming_amount || 0).toLocaleString()}`, icon: CalendarClock, color: 'text-cyan-400', testId: 'ud-upcoming' },
    { title: 'Due Today', value: `${s.today_due_count || 0} · PKR ${(s.today_due_amount || 0).toLocaleString()}`, icon: Clock, color: 'text-amber-500', testId: 'ud-today' },
    { title: 'Overdue', value: `${s.overdue_count || 0} · PKR ${(s.overdue_amount || 0).toLocaleString()}`, icon: AlertCircle, color: 'text-red-500', testId: 'ud-overdue' },
    { title: 'Paid Customers', value: `${s.paid_customers || 0}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'ud-paid' },
    ...(canSeeRemaining ? [{ title: 'Pending Amount', value: `PKR ${(s.remaining_amount || 0).toLocaleString()}`, icon: DollarSign, color: 'text-yellow-500', testId: 'ud-remaining' }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="upcoming-due-widget">
      <div className={`grid grid-cols-2 md:grid-cols-3 ${canSeeRemaining ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-3`}>
        {cards.map((c, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800" data-testid={c.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2 pt-3 px-3">
              <CardTitle className="text-xs font-medium text-slate-400">{c.title}</CardTitle>
              <c.icon className={`h-3.5 w-3.5 ${c.color}`} />
            </CardHeader>
            <CardContent className="pt-0 px-3 pb-3">
              <div className="text-lg font-bold text-white leading-tight">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white text-base">{title}</CardTitle></CardHeader>
        <CardContent>
          {(!data?.rows || data.rows.length === 0) ? (
            <div className="text-center py-6 text-slate-500 text-sm" data-testid="upcoming-due-empty">
              No upcoming or due installments in scope.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Customer Name</TableHead>
                    <TableHead className="text-slate-400">Account #</TableHead>
                    <TableHead className="text-slate-400">Mobile</TableHead>
                    <TableHead className="text-slate-400">Sales Executive</TableHead>
                    <TableHead className="text-slate-400">Installment Amt</TableHead>
                    {canSeeRemaining && <TableHead className="text-slate-400">Remaining</TableHead>}
                    <TableHead className="text-slate-400">Due Date</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.slice(0, 30).map((r, i) => (
                    <TableRow key={i}
                      onClick={isReadOnlyRow
                        ? undefined
                        : () => r.customer_id && setDetails({ open: true, customerId: r.customer_id, installmentId: r.installment_id })}
                      className={`border-slate-800 ${isReadOnlyRow ? '' : 'hover:bg-slate-800/60 cursor-pointer'}`}
                      data-testid="upcoming-due-row"
                      title={isReadOnlyRow ? 'View-only — contact your Branch Manager to open the profile' : 'Click to view full customer details'}>
                      <TableCell className={`text-white ${isReadOnlyRow ? '' : 'underline decoration-slate-700 hover:decoration-amber-500'}`}>{r.customer_name}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{r.account_number || '—'}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{r.mobile || '—'}</TableCell>
                      <TableCell className="text-emerald-400 text-xs font-medium" data-testid="upcoming-due-sales-exec">
                        {r.sales_executive_name || <span className="text-slate-600">— Unassigned</span>}
                      </TableCell>
                      <TableCell className="text-amber-500">PKR {(r.due_amount || 0).toLocaleString()}</TableCell>
                      {canSeeRemaining && (
                        <TableCell className="text-yellow-500 font-semibold" data-testid="upcoming-due-remaining">
                          PKR {(r.remaining_amount || 0).toLocaleString()}
                        </TableCell>
                      )}
                      <TableCell className="text-slate-300 text-xs">
                        {r.due_date ? new Date(r.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-1 rounded-sm text-xs uppercase font-medium ${STATUS_COLORS[r.bucket] || 'bg-slate-700/40 text-slate-300'}`}>
                            {r.bucket}
                          </span>
                          {r.bucket === 'overdue' && (
                            <button type="button"
                              onClick={(e) => handleRemind(e, r)}
                              data-testid={`remind-${r.installment_id}`}
                              title="Remind Next Month"
                              className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-500 hover:bg-amber-500 hover:text-slate-950 border border-amber-500/40 transition">
                              <BellPlus className="h-3 w-3" /> Next month
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CustomerDetailsDialog
        open={details.open}
        customerId={details.customerId}
        installmentId={details.installmentId}
        onOpenChange={(o) => setDetails((d) => ({ ...d, open: o }))}
      />
    </div>
  );
}
